import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';
import { sanitizeData } from '../utils/apiHelpers';

const getToken = () => localStorage.getItem('token');

const cashReservesApiClient = axios.create({
  baseURL: config.api.cashReservesServiceUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
});

cashReservesApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers['Authorization'] = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => {
    console.error('Cash Reserves API Client Request interceptor error:', error);
    return Promise.reject(error);
  }
);

cashReservesApiClient.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = sanitizeData(response.data);
    }
    return response;
  },
  (error) => {
    console.error(
      'Cash Reserves API Client Response Error:',
      error.response?.status,
      error.config?.url
    );
    return Promise.reject(error);
  }
);

// On 401, refresh the token and retry once before logging out.
applyAuthRetryInterceptor(cashReservesApiClient);
// Proactively refresh the token when it is close to expiring.
applyTokenRefreshInterceptor(cashReservesApiClient);

// Build a query string from an options object, omitting empty values.
const buildParams = (options = {}) => {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const cashReservesService = {
  /**
   * Health probe reporting how many reserve accounts load.
   * @returns {Promise<{status: string, reserve_rows_available: number, reserve_account_count: number}>}
   */
  getHealth: async () => {
    try {
      const response = await cashReservesApiClient.get('/cash-reserves/health');
      return response.data;
    } catch (error) {
      console.error('Error fetching cash reserves health:', error);
      throw error;
    }
  },

  /**
   * Get every account that has a configured cash reserve.
   * Serves the precomputed daily snapshot; pass { refresh: true } to force a
   * live recompute (and refresh the stored snapshot).
   * @param {Object} [options] - { refresh }
   * @returns {Promise<{account_count: number, accounts: Object[], source: string}>}
   */
  getReserveAccounts: async (options = {}) => {
    try {
      const params = options.refresh ? '?refresh=true' : '';
      const response = await cashReservesApiClient.get(`/cash-reserves/accounts${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reserve accounts:', error);
      throw error;
    }
  },

  /**
   * Get a reserve account's transactions over the recent window (default 30 days).
   * @param {string} accountNumber - The account to fetch transactions for.
   * @param {Object} [options] - { days, as_of }
   * @returns {Promise<Object>} { transactions: [...], transaction_summary: {...}, reserve: {...} }
   */
  getAccountTransactions: async (accountNumber, options = {}) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/transactions${buildParams(options)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching transactions for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get one family's daily holdings history and transaction event overlays.
   * @param {string} familyId - Tamarac family identifier (rebalancing group).
   * @param {Object} [options] - { start_date, end_date }
   * @returns {Promise<Object>} { daily: [...], events: [...] }
   */
  getFamilyHoldingsHistory: async (familyId, options = {}) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/family/${encodeURIComponent(familyId)}/holdings-history${buildParams(options)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching holdings history for family ${familyId}:`, error);
      throw error;
    }
  },

  /**
   * Mark an account as reviewed, snapshotting its current state.
   * @param {string} accountNumber - The account being reviewed.
   * @param {Object} [payload] - { note, days, as_of }
   * @returns {Promise<Object>} { saved: true, review: {...} }
   */
  markAccountReviewed: async (accountNumber, payload = {}) => {
    try {
      const response = await cashReservesApiClient.post(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/review`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error marking ${accountNumber} reviewed:`, error);
      throw error;
    }
  },

  /**
   * Get an account's review history (newest first).
   * @param {string} accountNumber - The account to fetch review history for.
   * @returns {Promise<{count: number, reviews: Object[]}>}
   */
  getAccountReviews: async (accountNumber) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/reviews`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching review history for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Undo an account's most recent review, deleting the record.
   * @param {string} accountNumber - The account whose latest review to remove.
   * @returns {Promise<{deleted: true, review: Object}>}
   */
  undoAccountReview: async (accountNumber) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/review`
      );
      return response.data;
    } catch (error) {
      console.error(`Error undoing review for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get the shared list of accounts omitted from the dashboard.
   * @returns {Promise<{count: number, ignored: Object[]}>}
   */
  getIgnoredAccounts: async () => {
    try {
      const response = await cashReservesApiClient.get('/cash-reserves/ignore');
      return response.data;
    } catch (error) {
      console.error('Error fetching ignored accounts:', error);
      throw error;
    }
  },

  /**
   * Add an account to the shared ignore list.
   * @param {string} accountNumber - The account to ignore.
   * @param {Object} [payload] - { account_name, note }
   * @returns {Promise<{saved: true, ignored: Object}>}
   */
  addIgnoredAccount: async (accountNumber, payload = {}) => {
    try {
      const response = await cashReservesApiClient.post('/cash-reserves/ignore', {
        ...payload,
        account_number: accountNumber,
      });
      return response.data;
    } catch (error) {
      console.error(`Error ignoring ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Save a transaction's annotation, replacing its prior note and links.
   * Links reference the account's cash reserves by stable reserve_id, each
   * with an optional allocated dollar amount.
   * @param {string} accountNumber - The account the transaction belongs to.
   * @param {string} transactionId - The transaction to annotate.
   * @param {Object} payload -
   *   { note, note_reason_category, links: [{ reserve_id, allocated_amount }] }
   * @returns {Promise<{saved: true, transaction_note: Object}>}
   */
  saveTransactionNote: async (accountNumber, transactionId, payload = {}) => {
    try {
      const response = await cashReservesApiClient.put(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/transaction/${encodeURIComponent(transactionId)}/note`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error saving note for transaction ${transactionId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a transaction's note (and reserve link).
   * @param {string} accountNumber - The account the transaction belongs to.
   * @param {string} transactionId - The transaction whose note to delete.
   * @returns {Promise<{deleted: true, transaction_note: Object}>}
   */
  deleteTransactionNote: async (accountNumber, transactionId) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/transaction/${encodeURIComponent(transactionId)}/note`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting note for transaction ${transactionId}:`, error);
      throw error;
    }
  },

  /**
   * Remove an account from the ignore list so it reappears on the dashboard.
   * @param {string} accountNumber - The account to stop ignoring.
   * @returns {Promise<{deleted: true, ignored: Object}>}
   */
  removeIgnoredAccount: async (accountNumber) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/ignore/${encodeURIComponent(accountNumber)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error un-ignoring ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get quarterly management-fee snapshots for several accounts in one request.
   * Serves the dashboard's snapshot section, which needs only these rows — far
   * cheaper than one reserve-lineage call per account.
   * @param {string[]} accountNumbers - Accounts to fetch (a whole family).
   * @returns {Promise<{accounts: Object.<string, Object[]>}>} Every requested
   *   account is keyed, with an empty array when it has no snapshots.
   */
  getFeeSnapshots: async (accountNumbers = []) => {
    // The endpoint requires at least one account, and buildParams drops an
    // empty value — so an empty list would send no query string at all and come
    // back 400. Answer it here instead, with the shape the endpoint would have
    // returned, so the helper is safe to call with its own default.
    if (accountNumbers.length === 0) return { accounts: {} };
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/fee-snapshots${buildParams({ accounts: accountNumbers.join(',') })}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching fee snapshots:', error);
      throw error;
    }
  },

  /**
   * Get an account's reserve lineage: current reserves, retired fingerprints,
   * user-declared identity links, suggested retired->current pairs, and the
   * quarterly management-fee snapshots (frozen quarter-end copies of the
   * Management Fee reserve — link targets, never Combine targets, which is why
   * they are their own key rather than part of `retired`).
   * @param {string} accountNumber - The account to inspect.
   * @returns {Promise<{current: Object[], retired: Object[], links: Object[], suggestions: Object[], fee_snapshots: Object[]}>}
   */
  getReserveLineage: async (accountNumber) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/reserve-lineage`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching reserve lineage for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Declare a retired reserve fingerprint and a current reserve the same
   * reserve (an explicit user action; combining is never automated).
   * @param {string} accountNumber - The account the reserves belong to.
   * @param {Object} payload - { retired_reserve_id, current_reserve_id }
   * @returns {Promise<{saved: true, reserve_id_link: Object}>}
   */
  saveReserveIdLink: async (accountNumber, payload) => {
    try {
      const response = await cashReservesApiClient.post(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/reserve-link`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error linking reserves for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Undo a reserve identity link.
   * @param {string} accountNumber - The account the link belongs to.
   * @param {string} retiredReserveId - The retired fingerprint to unlink.
   * @returns {Promise<{deleted: true, reserve_id_link: Object}>}
   */
  deleteReserveIdLink: async (accountNumber, retiredReserveId) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/account/${encodeURIComponent(accountNumber)}/reserve-link/${encodeURIComponent(retiredReserveId)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error unlinking reserves for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get the cash reserve creation queue — reserves that have been requested
   * but may not exist in Tamarac yet.
   *
   * Every open row arrives with a `verdict` ('created' | 'possible' |
   * 'not_found') and, where there is one, the `match` it was cross-referenced
   * against. That verdict is recomputed server-side on every call rather than
   * stored, so it is always current; nothing is ever auto-cleared.
   * @param {Object} [options] - { status } to filter to one state.
   * @returns {Promise<{count: number, open_count: number, requests: Object[]}>}
   */
  getReserveRequests: async (options = {}) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/requests${buildParams(options)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching the reserve request queue:', error);
      throw error;
    }
  },

  /**
   * Get the account numbers and reserve categories to suggest in the queue's
   * add form. Suggestions only — the form still accepts anything typed.
   * @returns {Promise<{accounts: {account_number: string, account_name: string}[],
   *   categories: string[]}>}
   */
  getReserveRequestOptions: async () => {
    try {
      const response = await cashReservesApiClient.get('/cash-reserves/request-options');
      return response.data;
    } catch (error) {
      console.error('Error fetching reserve request options:', error);
      throw error;
    }
  },

  /**
   * Queue a reserve by hand. The server resolves the account name, so the
   * response confirms whether the typed account number was the intended one.
   * @param {Object} payload - { account_number, reserve_category,
   *   reserve_amount, note }
   * @returns {Promise<{saved: true, request: Object}>}
   */
  addReserveRequest: async (payload) => {
    try {
      const response = await cashReservesApiClient.post('/cash-reserves/requests', payload);
      return response.data;
    } catch (error) {
      console.error('Error queueing a reserve request:', error);
      throw error;
    }
  },

  /**
   * Clear a request off the open queue.
   * @param {string} requestId - The request to resolve.
   * @param {Object} [payload] - { resolution: 'created' | 'dismissed', note }
   * @returns {Promise<{saved: true, request: Object}>}
   */
  resolveReserveRequest: async (requestId, payload = {}) => {
    try {
      const response = await cashReservesApiClient.post(
        `/cash-reserves/requests/${encodeURIComponent(requestId)}/resolve`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error resolving reserve request ${requestId}:`, error);
      throw error;
    }
  },

  /**
   * Undo a resolve, putting the request back on the open queue.
   * @param {string} requestId - The request to reopen.
   * @returns {Promise<{reopened: true, request: Object}>}
   */
  reopenReserveRequest: async (requestId) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/requests/${encodeURIComponent(requestId)}/resolve`
      );
      return response.data;
    } catch (error) {
      console.error(`Error reopening reserve request ${requestId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a request outright (a mis-filed entry, not a resolved one).
   * @param {string} requestId - The request to delete.
   * @returns {Promise<{deleted: true, request: Object}>}
   */
  deleteReserveRequest: async (requestId) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/requests/${encodeURIComponent(requestId)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting reserve request ${requestId}:`, error);
      throw error;
    }
  },

  /**
   * Get the (account, suggested-action type) pairs ignored on the dashboard.
   * Matching alerts are dropped server-side (dashboard, baselines, todo
   * capture) for that account only.
   * @returns {Promise<{count: number, ignores: Object[]}>}
   */
  getAlertIgnores: async () => {
    try {
      const response = await cashReservesApiClient.get('/cash-reserves/alert-ignores');
      return response.data;
    } catch (error) {
      console.error('Error fetching ignored suggestion types:', error);
      throw error;
    }
  },

  /**
   * Ignore a suggested-action type on one account (idempotent).
   * @param {string} action - The alert-engine action code (e.g. 'set_rmd_dates').
   * @param {string} accountNumber - The account to ignore it on.
   * @returns {Promise<{saved: true, ignore: Object}>}
   */
  addAlertIgnore: async (action, accountNumber) => {
    try {
      const response = await cashReservesApiClient.post('/cash-reserves/alert-ignores', {
        action,
        account_number: accountNumber,
      });
      return response.data;
    } catch (error) {
      console.error(`Error ignoring suggestion type ${action} on ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Re-enable a suggested-action type on one account; its alerts return on
   * the next load.
   * @param {string} action - The alert-engine action code to restore.
   * @param {string} accountNumber - The account it is ignored on.
   * @returns {Promise<{removed: true, ignore: Object}>}
   */
  removeAlertIgnore: async (action, accountNumber) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/alert-ignores/${encodeURIComponent(action)}`,
        { params: { account_number: accountNumber } }
      );
      return response.data;
    } catch (error) {
      console.error(`Error re-enabling suggestion type ${action} on ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get the Tamarac todo list — suggested actions resolved in the dashboard
   * (notes / reserve links, captured at Mark Reviewed) that still need the
   * matching manual change made in Tamarac. Exporting never deletes rows.
   * @param {Object} [options] - { status } to filter to one state
   *   ('open' | 'completed' | 'dismissed').
   * @returns {Promise<{count: number, open_count: number, todos: Object[]}>}
   */
  getTamaracTodos: async (options = {}) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/todos${buildParams(options)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching the Tamarac todo list:', error);
      throw error;
    }
  },

  /**
   * Take a todo off the open list.
   * @param {string} todoId - The todo to resolve.
   * @param {Object} [payload] - { resolution: 'completed' | 'dismissed', note }
   * @returns {Promise<{saved: true, todo: Object}>}
   */
  resolveTamaracTodo: async (todoId, payload = {}) => {
    try {
      const response = await cashReservesApiClient.post(
        `/cash-reserves/todos/${encodeURIComponent(todoId)}/resolve`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error resolving Tamarac todo ${todoId}:`, error);
      throw error;
    }
  },

  /**
   * Undo a resolve, putting the todo back on the open list.
   * @param {string} todoId - The todo to reopen.
   * @returns {Promise<{reopened: true, todo: Object}>}
   */
  reopenTamaracTodo: async (todoId) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/todos/${encodeURIComponent(todoId)}/resolve`
      );
      return response.data;
    } catch (error) {
      console.error(`Error reopening Tamarac todo ${todoId}:`, error);
      throw error;
    }
  },

  /**
   * Get the RMD Clients report: accounts whose owner is of RMD age by the end
   * of the year, every inherited IRA, and owner-type accounts with no matched
   * date of birth. Built from the daily Tamarac reports on each call.
   * @param {number} [year] - Defaults to the current year on the server.
   * @returns {Promise<{year: number, generated_at: string, dob_report_available: boolean,
   *   counts: Object, rmd_age: Object[], inherited: Object[], missing_dob: Object[],
   *   unclassified_types: {account_type: string, count: number}[]}>}
   */
  /**
   * Confirm or reject a guessed RMD owner match ("Check owner").
   * @param {string} accountNumber
   * @param {{decision: 'confirmed'|'rejected', client_key: string,
   *   owner_name?: string, account_name?: string}} body
   */
  reviewRmdOwner: async (accountNumber, body) => {
    try {
      const response = await cashReservesApiClient.put(
        `/cash-reserves/rmd-clients/owner-review/${encodeURIComponent(accountNumber)}`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error saving the RMD owner review:', error);
      throw error;
    }
  },

  /** Undo an account's RMD owner decision. */
  undoRmdOwnerReview: async (accountNumber) => {
    try {
      const response = await cashReservesApiClient.delete(
        `/cash-reserves/rmd-clients/owner-review/${encodeURIComponent(accountNumber)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error undoing the RMD owner review:', error);
      throw error;
    }
  },

  getRmdClients: async (year) => {
    try {
      const response = await cashReservesApiClient.get(
        `/cash-reserves/rmd-clients${buildParams({ year })}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching RMD clients:', error);
      throw error;
    }
  },
};

export default cashReservesService;
